/**
 * Pure helpers behind the unified client chat on the CSP side (Nikhil
 * 2026-09-12: each client user gets ONE private "Chat" thread in the portals;
 * the Infurnia Assistant answers first, reps take part in the same thread,
 * "Talk to a person" flags the thread). Exercised by user_chat.test.js
 * without a backend.
 *
 * Contract (cs-analysis, built in parallel):
 *   feed conversation  kind:'USER_CHAT', chatOwner:{id,name}, needsHumanAt:string|null
 *   feed query         ?kinds=USER_CHAT (private chats) | ?kinds=CLIENT_GROUP,ORG_GENERAL,TICKET (groups and
 *                      tickets); an api that predates the param ignores it, so rows are always re-split by kind
 *   feed hand-off      handoff:{askedAt, attendedAt|null, attendedByName|null} | null (the latest request)
 *   assistant message  authorType:'AI', meta:{kind:'assistant_answer', citations:[{id,title}],
 *                      confidence:'high'|'medium'|'low', grounded:boolean, feedback?:{helpful,at}}
 *   system message     authorType:'SYSTEM', meta.kind 'ticket_created' ({ticketId,ref,title}) | 'needs_human'
 *                      | 'whatsapp_linked' ({waContactId (masked), groups:[{channelId,title}]})
 *   annotated reply    meta:{kind:'annotation', ofMessageId} + replyToId
 *   verified link      chatOwner.verifiedVia:'self'|'rep'|null — 'self' = the user linked their own WhatsApp number
 *   backstop answer    assistant meta.via:'backstop' — the assistant answered after 10 min with no human reply
 *   revised message    CLIENT_USER meta:{kind:'revision', ofMessageId} — a NEW message the client sent instead
 *                      of editing an earlier one (history untouched; client panel "revise", 2026-09-15)
 */

export const USER_CHAT_KIND = 'USER_CHAT';

export const is_user_chat = (c) => !!c && c.kind === USER_CHAT_KIND;

/** Row title: the person for a user chat, else the org. */
export const chat_row_title = (c) => {
    if (!c) return '';
    if (is_user_chat(c)) {
        const owner = c.chatOwner && c.chatOwner.name ? String(c.chatOwner.name).trim() : '';
        return owner || c.title || 'Client user';
    }
    return c.clientName || '';
};

/** Row subtitle under a user chat: the org (the title already names the person). */
export const chat_row_org = (c) => (c && is_user_chat(c) ? (c.clientName || '') : '');

/** True when the chat owner linked their own WhatsApp number ('self'); a rep mapping ('rep') earns no tick. */
export const owner_verified = (c) => !!(c && c.chatOwner && c.chatOwner.verifiedVia === 'self');
export const VERIFIED_TICK_TITLE = 'Verified — this user linked their own WhatsApp number from the portal';

const parse_ts = (d) => {
    if (d == null || d === '') return null;
    const t = d instanceof Date ? d.getTime() : new Date(d).getTime();
    return Number.isFinite(t) ? t : null;
};

// ── inbox tabs ──────────────────────────────────────────────────────────────
// The Chat inbox is two tabs (Nikhil 2026-09-24): Groups (WhatsApp / org groups and tickets) and Private
// chats (each portal user's thread with the assistant and the CS team), the latter in three sections.

export const GROUPS_TAB = 'groups';
export const PRIVATE_TAB = 'private';
/** The kinds the Groups tab lists; every other non-private kind lands there too (inbox_tab_of). */
export const GROUP_KINDS = ['CLIENT_GROUP', 'ORG_GENERAL', 'TICKET'];
/** Private chats are fetched on their own, wider window so busy groups never push older ones out. */
export const PRIVATE_FEED_LIMIT = 300;

/** Which tab a feed row belongs to: 'private' for a user chat, 'groups' for everything else; null for no row. */
export const inbox_tab_of = (c) => (!c ? null : is_user_chat(c) ? PRIVATE_TAB : GROUPS_TAB);

/** GET /comms/feed query for one tab. The Groups tab keeps the api's default window. */
export const feed_query = (tab) => (tab === PRIVATE_TAB
    ? { kinds: USER_CHAT_KIND, limit: PRIVATE_FEED_LIMIT }
    : { kinds: GROUP_KINDS.join(',') });

/** GET /comms/search query for the open tab and pill: its kinds, so a search in Groups or Private chats stays in that
 *  tab (Nikhil 2026-09-25: the box lived in a tab but searched both), and the pill (my | team | all), so the hits
 *  follow it as the list does — the api only ever narrows what the role allows. */
export const search_query = (tab, q, scope) => ({ q, kinds: tab === PRIVATE_TAB ? USER_CHAT_KIND : GROUP_KINDS.join(','), ...(scope ? { scope } : {}) });

/** The search hits of the open tab only — an api without ?kinds on search returns every tab's hits. Pure. */
export const hits_in_tab = (rows, tab) => (Array.isArray(rows) ? rows : []).filter((r) => inbox_tab_of(r) === tab);

/** One conversation list out of the two tab fetches: the group rows of the Groups response (feed order),
 *  then the private rows of the Private response. Each response is re-split by kind — an api that ignores
 *  ?kinds returns a mixed feed — and a channel appears once. Pure. */
export const merge_tab_feeds = (group_rows, private_rows) => {
    const seen = new Set();
    const take = (rows, tab) => (Array.isArray(rows) ? rows : []).filter((c) => {
        if (!c || inbox_tab_of(c) !== tab || seen.has(c.channelId)) return false;
        seen.add(c.channelId);
        return true;
    });
    return take(group_rows, GROUPS_TAB).concat(take(private_rows, PRIVATE_TAB));
};

/** Does the row belong to the scope pill? my → my rows; team → my + team rows; all → every row. */
export const in_scope = (c, scope) => {
    if (!c) return false;
    if (scope === 'my') return c.scope === 'my';
    if (scope === 'team') return c.scope === 'my' || c.scope === 'team';
    return true;
};

const RECENT_MS = 7 * 864e5;

/** Does the row pass the Groups tab's pill (Unread only / My chats / Recent)? null = everything. */
export const matches_type_filter = (c, filter, now = Date.now()) => {
    if (!c || !filter) return !!c;
    switch (filter) {
        case 'unread': return !!c.unread;
        case 'mine': return !!c.participating;
        case 'recent': {
            const t = parse_ts(c.lastMessageAt);
            return t != null && now - t <= RECENT_MS;
        }
        default: return true;
    }
};

/** Which Private-chats section a user chat sits in: 'waiting' (asked for a person, no reply yet),
 *  'answered' (the latest request was attended), 'assistant' (every other chat); null for non-private rows. */
export const private_section_of = (c) => {
    if (!is_user_chat(c)) return null;
    if (c.needsHumanAt) return 'waiting';
    return handoff_attended(c) ? 'answered' : 'assistant';
};

// Stable sort on a timestamp; rows without a parseable one go last, in their input order.
const sort_by_ts = (rows, ts_of, dir) => rows
    .map((c, i) => ({ c, i, t: ts_of(c) }))
    .sort((a, b) => {
        if (a.t == null && b.t == null) return a.i - b.i;
        if (a.t == null) return 1;
        if (b.t == null) return -1;
        return (dir === 'asc' ? a.t - b.t : b.t - a.t) || a.i - b.i;
    })
    .map((x) => x.c);

/** The Private chats tab: { waiting (oldest ask first), answered (latest ask first), assistant (latest
 *  activity first) }. Non-private and junk rows are dropped. Pure — never mutates the input. */
export const private_chat_sections = (rows) => {
    const by = { waiting: [], answered: [], assistant: [] };
    (Array.isArray(rows) ? rows : []).forEach((c) => {
        const s = private_section_of(c);
        if (s) by[s].push(c);
    });
    return {
        waiting: sort_by_ts(by.waiting, (c) => parse_ts(c.needsHumanAt), 'asc'),
        answered: sort_by_ts(by.answered, (c) => {
            const asked = parse_ts(c.handoff.askedAt);
            return asked != null ? asked : parse_ts(c.handoff.attendedAt);
        }, 'desc'),
        assistant: sort_by_ts(by.assistant, (c) => parse_ts(c.lastMessageAt), 'desc'),
    };
};

/** Tab badges, each { count, waiting } or null (no badge): Groups → the conversations with unread
 *  messages; Private chats → the chats waiting for a person (waiting: true, shown "🙋 N") while there
 *  are any, else the chats with unread messages. */
export const inbox_tab_badges = (rows) => {
    const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
    // Unread in conversations the representative takes part in: a never-opened channel counts its whole
    // history as unread (the row's quiet "new" chip), and representatives live in WhatsApp, so counting those
    // would put nearly every group in the badge.
    const unread = (tab) => list.filter((c) => inbox_tab_of(c) === tab && c.unread > 0 && c.participating).length;
    const waiting = list.filter((c) => private_section_of(c) === 'waiting').length;
    const groups = unread(GROUPS_TAB);
    const private_unread = unread(PRIVATE_TAB);
    return {
        groups: groups ? { count: groups, waiting: false } : null,
        private: waiting ? { count: waiting, waiting: true }
            : private_unread ? { count: private_unread, waiting: false } : null,
    };
};

/** Row identity glyph: 👤 for a person's private chat, 👥 for a WhatsApp / org
 *  group; null for tickets (their "Ticket" label already says what they are). */
export const kind_icon = (c) => {
    if (!c) return null;
    if (is_user_chat(c)) return { glyph: '👤', label: 'private chat' };
    if (c.kind === 'CLIENT_GROUP') return { glyph: '👥', label: 'WhatsApp group' };
    return null;
};

/** The client page's Chats tab order: private chats waiting for a person first, oldest ask first;
 *  everything else keeps the feed's own order (latest activity first). A row without a parseable
 *  needsHumanAt is not "waiting". Pure — never mutates the input. */
export const order_conversations = (list) => {
    const rows = Array.isArray(list) ? list.filter(Boolean) : [];
    const waiting = rows
        .map((c, i) => ({ c, i, t: is_user_chat(c) ? parse_ts(c.needsHumanAt) : null }))
        .filter((x) => x.t != null)
        .sort((a, b) => a.t - b.t || a.i - b.i)
        .map((x) => x.c);
    const rest = rows.filter((c) => !(is_user_chat(c) && parse_ts(c.needsHumanAt) != null));
    return waiting.concat(rest);
};

/** The client page's Chats tab (CT-15539): an org's private threads, feed-row shape,
 *  newest activity first, under the feed's access rule. 404 until the api ships it. */
export const client_chats_path = (store_id) => `/api/v1/comms/clients/${encodeURIComponent(String(store_id))}/chats`;

/** "3m" / "1h 05m" / "2d 3h" — how long a chat has waited for a person.
 *  Sub-minute → "<1m". Unparseable or future timestamps → ''. */
export const wait_label = (needs_human_at, now = Date.now()) => {
    const t = parse_ts(needs_human_at);
    if (t == null) return '';
    const ms = (typeof now === 'number' ? now : parse_ts(now)) - t;
    if (!(ms >= 0)) return '';
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

/** Wait-badge urgency: 'fresh' (<15m), 'warm' (<1h), 'hot' (1h+) — colours the row badge. */
export const wait_band = (needs_human_at, now = Date.now()) => {
    const t = parse_ts(needs_human_at);
    if (t == null) return null;
    const mins = ((typeof now === 'number' ? now : parse_ts(now)) - t) / 60000;
    if (!(mins >= 0)) return null;
    return mins < 15 ? 'fresh' : mins < 60 ? 'warm' : 'hot';
};

/** "Needs a person since HH:MM" (today) or "since 03 Sep, 14:05" — for the thread header. */
export const needs_human_since = (needs_human_at, now = new Date()) => {
    const t = parse_ts(needs_human_at);
    if (t == null) return '';
    const d = new Date(t);
    const ref = typeof now === 'number' ? new Date(now) : now;
    const same_day = d.toDateString() === ref.toDateString();
    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    if (same_day) return hhmm;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]}, ${hhmm}`;
};

/** Clear needsHumanAt on one row and mark its request answered (optimistic — a representative's reply
 *  does both server-side; the row moves from "Needs a person" to "Answered", marked; Aesha 2026-09-24).
 *  `by` = the replying representative's name when known. */
export const clear_needs_human = (conversations, channel_id, by = null, now = new Date()) =>
    (Array.isArray(conversations) ? conversations : []).map((c) => {
        if (!(c && c.channelId === channel_id && c.needsHumanAt)) return c;
        const asked = (c.handoff && c.handoff.askedAt) || c.needsHumanAt;
        return { ...c, needsHumanAt: null, handoff: { askedAt: asked, attendedAt: new Date(now).toISOString(), attendedByName: by || null } };
    });

/** The answered request of a row — { at, by } — or null (never asked, or still waiting). */
export const handoff_attended = (c) => {
    if (!c || c.needsHumanAt || !c.handoff || !c.handoff.attendedAt) return null;
    return { at: c.handoff.attendedAt, by: c.handoff.attendedByName || null };
};

/** "Attended by Madhurima" / "Attended" — the mark on an answered request. */
export const attended_label = (c) => {
    const a = handoff_attended(c);
    if (!a) return '';
    return a.by ? `Attended by ${String(a.by).split(' ')[0]}` : 'Attended';
};

// ── message meta ────────────────────────────────────────────────────────────

export const KNOWLEDGE_UNIT_PATH = '/home/knowledge';
export const CLIENT_TICKET_PATH = '/home/client_tickets';
export const CHAT_PATH = '/home/chat';
/** Shown on assistant answers with meta.via 'backstop'. No number: the wait is
 *  CLIENT_CHAT_POLICY.backstopMs and it is tuned server-side (10 min → 2 min on
 *  2026-09-14), so a figure written here goes stale without anyone noticing. */
export const BACKSTOP_NOTE = 'answered when no person replied';

/** Knowledge-page deep link for one cited unit. */
export const citation_href = (unit_id) => `${KNOWLEDGE_UNIT_PATH}?unit=${encodeURIComponent(String(unit_id))}`;

export const is_assistant_message = (m) => !!m && m.authorType === 'AI';
export const is_system_message = (m) => !!m && m.authorType === 'SYSTEM';

/** Normalised assistant meta, or null when the message is not an assistant answer.
 *  Citations without an id are dropped (nothing to link); a title falls back to the id. */
export const assistant_meta = (m) => {
    if (!is_assistant_message(m)) return null;
    const meta = m.meta && typeof m.meta === 'object' ? m.meta : {};
    const citations = (Array.isArray(meta.citations) ? meta.citations : [])
        .filter((c) => c && c.id != null && String(c.id).trim())
        .map((c) => ({ id: String(c.id), title: c.title ? String(c.title) : String(c.id), href: citation_href(c.id) }));
    const confidence = ['high', 'medium', 'low'].indexOf(meta.confidence) >= 0 ? meta.confidence : null;
    const feedback = meta.feedback && typeof meta.feedback === 'object' && typeof meta.feedback.helpful === 'boolean'
        ? { helpful: meta.feedback.helpful, at: meta.feedback.at || null }
        : null;
    // Since cs-analysis 7dfa6b3 the assistant carries the conversation itself:
    // greetings, "what can you do?", clarifying questions. Those rows are
    // grounded:false by construction and are not abstentions, so the thread
    // must not flag them. `triage` names why the row exists.
    const triage = TRIAGE_CLASSES.indexOf(meta.triage) >= 0 ? meta.triage : null;
    return {
        kind: meta.kind || 'assistant_answer',
        citations,
        confidence,
        grounded: meta.grounded !== false,
        feedback,
        via: meta.via === 'backstop' || meta.via === 'media' ? meta.via : null,
        // The question was what the client sent, not typed (cs-analysis §6.9):
        // a voice note / recording transcript or a screenshot description.
        fromMedia: meta.fromMedia === 'transcript' || meta.fromMedia === 'description' ? meta.fromMedia : null,
        triage,
        conversational: triage !== null && CONVERSATIONAL_TRIAGE.indexOf(triage) >= 0,
    };
};

/** The assistant's triage classes (docs/design-client-comms.md §6.7). */
export const TRIAGE_CLASSES = ['chitchat', 'meta', 'clarification_reply', 'product_question', 'human_request', 'unclear'];
/** Rows that are conversation, not knowledge: never "ungrounded", nothing to verify. */
export const CONVERSATIONAL_TRIAGE = ['chitchat', 'meta', 'unclear'];
/** Short thread label per class; null → say nothing. */
export const triage_label = (triage) => ({ chitchat: 'conversation', meta: 'about the assistant', unclear: 'asked for detail' })[triage] || null;

/** Normalised system meta: {kind, text, href}. Unknown kinds fall back to the body. */
export const system_meta = (m) => {
    if (!is_system_message(m)) return null;
    const meta = m.meta && typeof m.meta === 'object' ? m.meta : {};
    if (meta.kind === 'ticket_created') {
        // meta.ref is the human ref from the backend ('CT-42'); older rows carried the bare number.
        const raw = meta.ref != null && meta.ref !== '' ? String(meta.ref) : '';
        const ref = raw ? (/^CT-/i.test(raw) ? raw.toUpperCase() : `CT-${raw}`) : 'Ticket';
        const title = meta.title ? String(meta.title) : '';
        return {
            kind: 'ticket_created',
            text: `${ref} raised${title ? `: ${title}` : ''}`,
            href: meta.ticketId ? `${CLIENT_TICKET_PATH}?open=${encodeURIComponent(String(meta.ticketId))}` : null,
        };
    }
    if (meta.kind === 'needs_human') {
        return { kind: 'needs_human', text: m.body || 'The client asked to talk to a person', href: null };
    }
    if (meta.kind === 'whatsapp_linked') {
        // The portal user linked their WhatsApp number; the backend names the
        // groups the number was added to. waContactId arrives masked.
        const contact = meta.waContactId != null && String(meta.waContactId).trim() ? String(meta.waContactId).trim() : '';
        const groups = (Array.isArray(meta.groups) ? meta.groups : [])
            .filter((g) => g && (g.channelId != null || g.title))
            .map((g) => ({
                channelId: g.channelId != null ? String(g.channelId) : null,
                title: g.title ? String(g.title) : 'group',
                href: g.channelId != null ? `${CHAT_PATH}?channel=${encodeURIComponent(String(g.channelId))}` : null,
            }));
        const lead = `WhatsApp${contact ? ` ${contact}` : ''} linked`;
        return {
            kind: 'whatsapp_linked',
            lead,
            groups,
            text: lead + (groups.length ? ` · added to ${groups.map((g) => g.title).join(', ')}` : ''),
            href: null,
        };
    }
    return { kind: meta.kind || null, text: m.body || '', href: null };
};

/** {ofMessageId} for an annotated-image reply, else null. */
export const annotation_of = (m) => {
    if (!m || !m.meta || typeof m.meta !== 'object' || m.meta.kind !== 'annotation') return null;
    return { ofMessageId: m.meta.ofMessageId != null ? String(m.meta.ofMessageId) : (m.replyToId || null) };
};

/** How much of the original a "revised from" line quotes. */
export const REVISION_EXCERPT = 80;

/** What the line quotes of the original: its body on one line, cut at
 *  REVISION_EXCERPT; "(deleted)" / "(attachment)" as in the quote-reply block. */
const revision_excerpt = (original) => {
    if (!original) return '';
    if (original.deletedAt) return '(deleted)';
    const body = String(original.body || '').replace(/\s+/g, ' ').trim();
    if (!body) return Array.isArray(original.mediaRefs) && original.mediaRefs.length ? '(attachment)' : '';
    const cut = body.length > REVISION_EXCERPT ? `${body.slice(0, REVISION_EXCERPT).replace(/\s+$/, '')}…` : body;
    return `“${cut}”`;
};

/** Client "revise" (client panel, 2026-09-15): instead of editing a message
 *  the client sends a NEW one carrying meta {kind:'revision', ofMessageId} —
 *  history stays as it was and the api passes the meta through to staff
 *  surfaces. For such a row → {ofMessageId, original, text}; `text` is the
 *  compact line the thread shows above the body: "↻ revised from “…”" with the
 *  first REVISION_EXCERPT chars of the original when it is in the loaded window
 *  (`by_id`: id → message), else just "↻ revised". Null for any other row. */
/** What an attachment says (cs-analysis §6.9): the message's insight for
 *  attachment `idx` — { kind: 'transcript'|'description', text } — or null while
 *  the worker pump has not written it (about a minute after the message).
 *  Nikhil, 2026-09-15: "is the AI listening and responding to voice recordings?
 *  does it look at screenshots or recordings too?" — now it does, and the rep
 *  reads the same words under the player. */
export const insight_for = (m, idx) => {
    const list = m && Array.isArray(m.insights) ? m.insights : [];
    const hit = list.find((i) => i && i.idx === idx && (i.kind === 'transcript' || i.kind === 'description') && typeof i.text === 'string' && i.text.trim());
    return hit ? { kind: hit.kind, text: hit.text.trim() } : null;
};

/** The collapsed line's label: a video's transcript is still a transcript. */
export const insight_label = (kind) => (kind === 'description' ? 'what the screenshot shows' : 'transcript');

/** The assistant meta-line marker for an answer to media; null for typed questions. */
export const from_media_note = (from_media, mimetype) => {
    if (from_media === 'description') return '🖼 answered a screenshot';
    if (from_media === 'transcript') return String(mimetype || '').indexOf('video/') === 0 ? '🎙 answered a recording' : '🎙 answered a voice note';
    return null;
};

export const revision_of = (m, by_id) => {
    if (!m || !m.meta || typeof m.meta !== 'object' || m.meta.kind !== 'revision') return null;
    const raw = m.meta.ofMessageId;
    const of = raw != null && String(raw).trim() ? String(raw).trim() : null;
    const original = (of && by_id && typeof by_id === 'object' && by_id[of]) || null;
    const excerpt = revision_excerpt(original);
    return { ofMessageId: of, original, text: excerpt ? `↻ revised from ${excerpt}` : '↻ revised' };
};

/** First image attachment of a message → {idx, ref}, else null. */
export const first_image_ref = (m) => {
    const refs = m && Array.isArray(m.mediaRefs) ? m.mediaRefs : [];
    for (let i = 0; i < refs.length; i += 1) {
        const mm = ((refs[i] && refs[i].mimetype) || '').toLowerCase();
        if (mm.indexOf('image/') === 0) return { idx: i, ref: refs[i] };
    }
    return null;
};

/** Multipart fields for an annotated-image reply (POST /channels/:id/media). */
export const annotation_upload_fields = (of_message_id) => ({
    replyToId: String(of_message_id),
    meta: JSON.stringify({ kind: 'annotation', ofMessageId: String(of_message_id) }),
});

/** True when an upload response is a posted message (has a seq) rather than a
 *  staged {mediaId} — decides whether a follow-up POST /messages is needed. */
export const upload_posted_message = (resp) => !!(resp && (resp.seq != null || (resp.message && resp.message.seq != null)));
